/**
 * 战机的规则事件 → 飘字、读屏播报、驾驶舱里 TA 的反应。纯函数：给事件和事件发生后的状态，交回要画的字、要念的话、TA 此刻的心情。
 * 驾驶员的动作段名等 P 定（F0 待补）；在那之前，驾驶舱按心情给 TA 的形象图做表情与动作（flight.css），不猜段名。
 * 心情照 B 合同第 3 节的建议：起飞、换波专注；击落小得意；连击越连越兴奋；擦身而过紧张擦汗；被撞慌；绝招攒满跃跃欲试；放绝招大动作；通关、失败。
 * 失败只是这一局的结果，不说受伤，也不写成真实的驾驶经历。用词只说游戏里发生了什么，不出现内部代码。
 */
import type { FlightEventBody, FlightState } from "../core";
import type { FloatText, FloatTone } from "./feedback";

export type PilotMood = "calm" | "focus" | "proud" | "excited" | "nervous" | "ouch" | "ready" | "special" | "cleared" | "lost";

export interface Pilot {
  mood: PilotMood;
  sinceMs: number;
}

/** 一阵反应停多久：之后回到平静（绝招攒满时回到跃跃欲试）；通关、失败不回去 */
export const PILOT_REACT_MS = 1000;

/** 同一阵里谁压得住谁：被撞最显眼，后来的小事盖不掉它 */
const RANK: Record<PilotMood, number> = { calm: 0, ready: 1, focus: 1, proud: 2, nervous: 3, excited: 4, special: 5, ouch: 6, cleared: 9, lost: 9 };

const MOOD_OF: Partial<Record<FlightEventBody["type"], PilotMood>> = {
  started: "focus",
  wave_started: "focus",
  enemy_destroyed: "proud",
  combo: "excited",
  near_miss: "nervous",
  hit: "ouch",
  special_ready: "ready",
  special: "special",
};

export const startingPilot: Pilot = { mood: "calm", sinceMs: 0 };

/** 此刻 TA 的心情：一阵反应过了时候就回到平静（绝招攒满时是跃跃欲试） */
export function pilotNow(pilot: Pilot, state: FlightState, nowMs: number): PilotMood {
  if (pilot.mood === "cleared" || pilot.mood === "lost") return pilot.mood;
  if (nowMs - pilot.sinceMs < PILOT_REACT_MS) return pilot.mood;
  return state.special >= 1 ? "ready" : "calm";
}

/** 一条事件之后 TA 的心情；同一阵里分量轻的事盖不掉分量重的（被撞了又击落一架，还是慌）。一阵过了，pilotNow 已回到平静，什么事都能换上来。开局（含再来一局）一律专注。 */
export function pilotAfter(event: FlightEventBody, pilot: Pilot, state: FlightState, nowMs: number): Pilot {
  if (event.type === "ended") {
    if (event.reason === "quit") return pilot;
    return { mood: event.result.cleared ? "cleared" : "lost", sinceMs: nowMs };
  }
  if (event.type === "started") return { mood: "focus", sinceMs: nowMs };
  const mood = MOOD_OF[event.type];
  if (!mood) return pilot;
  const current = pilotNow(pilot, state, nowMs);
  if (RANK[current] > RANK[mood]) return pilot;
  return { mood, sinceMs: nowMs };
}

/** 一条事件要飘的字；击落的分数飘在那架最后出现的地方（lastSeen 是推进前各敌机的位置）。 */
export function flightFloatsFor(
  event: FlightEventBody,
  state: FlightState,
  lastSeen: ReadonlyMap<string, { x: number; y: number }>,
  nowMs: number,
  nextId: () => number,
): FloatText[] {
  const at = (text: string, tone: FloatTone, x: number, y: number): FloatText => ({ id: nextId(), text, tone, x, y, bornMs: nowMs });
  const nearPlane = (text: string, tone: FloatTone, lift = 0) => at(text, tone, state.plane.x, state.plane.y - state.plane.radius - 10 - lift);
  switch (event.type) {
    case "enemy_destroyed": {
      const where = lastSeen.get(event.enemyId) ?? { x: state.plane.x, y: state.plane.y - 80 };
      return [at(`+${event.points}`, "good", where.x, where.y)];
    }
    case "combo":
      return [nearPlane(`连击 ×${event.count}`, "good", 18)];
    case "near_miss":
      return [nearPlane("好险", "info")];
    case "hit":
      return [nearPlane("−1 ♥", "bad")];
    case "special":
      return [at(`绝招！+${event.points}`, "good", 180, 300)];
    case "wave_started":
      return [at(`第 ${event.wave} 波`, "info", 180, 120)];
    default:
      return [];
  }
}

/** 读屏播报：只念改变局面的事；一架一架击落、擦身而过靠画面与驾驶舱，不逐个念。第一波跟着开局，不另念。 */
export function flightAnnouncementFor(event: FlightEventBody, petName: string): string | null {
  switch (event.type) {
    case "started":
      return `开始了。带${petName}开飞机：拖着飞机躲开来袭，它会自己开火，撑满 ${Math.round(event.durationMs / 1000)} 秒。`;
    case "wave_started":
      return event.wave > 1 ? `第 ${event.wave} 波来了。` : null;
    case "combo":
      return `连击 ${event.count}！`;
    case "hit":
      return event.heartsLeft > 0 ? `被撞到了，还剩 ${event.heartsLeft} 颗心。` : "被撞到了。";
    case "special_ready":
      return "绝招攒满了，可以放了。";
    case "special":
      return `放出绝招，清掉 ${event.cleared} 个。`;
    case "ended":
      if (event.reason === "quit") return null;
      return event.reason === "time_up" ? `通关！一共 ${event.result.score} 分。` : `心用完了，一共 ${event.result.score} 分。`;
    default:
      return null;
  }
}
